import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";

import { PublicLayout } from "@/components/layout/PublicLayout";
import { ClientLayout } from "@/components/layout/ClientLayout";
import { AdminLayout } from "@/components/layout/AdminLayout";

import HomePage from "@/pages/public/HomePage";
import BoutiquePage from "@/pages/public/BoutiquePage";
import CategoryPage from "@/pages/public/CategoryPage";
import ProductPage from "@/pages/public/ProductPage";
import SelectionPage from "@/pages/public/SelectionPage";
import DemandePage from "@/pages/public/DemandePage";
import CoordinationPage from "@/pages/public/CoordinationPage";
import RendezVousPage from "@/pages/public/RendezVousPage";
import RealisationsPage from "@/pages/public/RealisationsPage";
import RealisationDetailPage from "@/pages/public/RealisationDetailPage";
// import AvisPage from "@/pages/public/AvisPage"; // Masquée temporairement — avis de démonstration uniquement (CMS avis = P2). Architecture conservée.
import BlogPage from "@/pages/public/BlogPage";
import BlogArticlePage from "@/pages/public/BlogArticlePage";
import HistoirePage from "@/pages/public/HistoirePage";
import PartenairesPage from "@/pages/public/PartenairesPage";
import ContactPage from "@/pages/public/ContactPage";
import AuthPage from "@/pages/public/AuthPage";
import PaymentReturnPage from "@/pages/public/PaymentReturnPage";
import MentionsLegalesPage from "@/pages/public/MentionsLegalesPage";
import PolitiqueConfidentialitePage from "@/pages/public/PolitiqueConfidentialitePage";
import PolitiqueCookiesPage from "@/pages/public/PolitiqueCookiesPage";
import CgvPage from "@/pages/public/CgvPage";
import ResetPasswordPage from "@/pages/public/ResetPasswordPage";

import ClientDashboard from "@/pages/client/ClientDashboard";
import EventDetailPage from "@/pages/client/EventDetailPage";
import ClientProfilePage from "@/pages/client/ClientProfilePage";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import ReservationsPage from "@/pages/admin/ReservationsPage";
import ReservationDetailPage from "@/pages/admin/ReservationDetailPage";
import CatalogPage from "@/pages/admin/CatalogPage";
import PlanningPage from "@/pages/admin/PlanningPage";
import DeliveriesPage from "@/pages/admin/DeliveriesPage";
import MessagesPage from "@/pages/admin/MessagesPage";
import PaymentsPage from "@/pages/admin/PaymentsPage";
import NotificationsPage from "@/pages/admin/NotificationsPage";
import SettingsPage from "@/pages/admin/SettingsPage";
import ReturnsPage from "@/pages/admin/ReturnsPage";
import IncidentsPage from "@/pages/admin/IncidentsPage";
import ClientsPage from "@/pages/admin/ClientsPage";
import ClientDetailPage from "@/pages/admin/ClientDetailPage";
import QuotesPage from "@/pages/admin/QuotesPage";
import AppointmentsPage from "@/pages/admin/AppointmentsPage";
import CoordinationAdminPage from "@/pages/admin/CoordinationAdminPage";
import CoordinationDetailPage from "@/pages/admin/CoordinationDetailPage";
import GalleryAdminPage from "@/pages/admin/GalleryAdminPage";
import BlogAdminPage from "@/pages/admin/BlogAdminPage";
import PartnersAdminPage from "@/pages/admin/PartnersAdminPage";
import MediaAdminPage from "@/pages/admin/MediaAdminPage";
import FormulesCoordinationAdminPage from "@/pages/admin/FormulesCoordinationAdminPage";
import TermsAdminPage from "@/pages/admin/TermsAdminPage";
import PromoCodesPage from "@/pages/admin/PromoCodesPage";
import EmailsAdminPage from "@/pages/admin/EmailsAdminPage";
import AdminPlaceholder from "@/pages/admin/AdminPlaceholder";
import PagesSitePage from "@/pages/admin/PagesSitePage";
import ClientReservationPage from "@/pages/client/ClientReservationPage";
import ParametresPage from "@/pages/admin/ParametresPage";
import UtilisateursPage from "@/pages/admin/UtilisateursPage";
import VenuesAdminPage from "@/pages/admin/VenuesAdminPage";
import LieuxPage from "@/pages/public/LieuxPage";
import ReviewsAdminPage from "@/pages/admin/ReviewsAdminPage";

function RequireAuth({ children, admin = false }) {
  const { user } = useAuth();
  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream font-heading text-deepgreen" data-testid="auth-loading">
        Chargement…
      </div>
    );
  }
  if (!user) return <Navigate to="/connexion" replace />;
  if (admin && !["super_admin", "collaborator", "logistics"].includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/boutique" element={<BoutiquePage />} />
            <Route path="/boutique/:categorySlug" element={<CategoryPage />} />
            <Route path="/produit/:slug" element={<ProductPage />} />
            <Route path="/ma-selection" element={<SelectionPage />} />
            <Route path="/demande" element={<DemandePage />} />
            <Route path="/coordination" element={<CoordinationPage />} />
            <Route path="/rendez-vous" element={<RendezVousPage />} />
            <Route path="/realisations" element={<RealisationsPage />} />
            <Route path="/realisations/:slug" element={<RealisationDetailPage />} />
            {/* Route /avis masquée temporairement — avis de démonstration uniquement (CMS avis = P2) */}
            <Route path="/avis" element={<Navigate to="/" replace />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogArticlePage />} />
            <Route path="/mon-histoire" element={<HistoirePage />} />
            <Route path="/partenaires" element={<PartenairesPage />} />
            <Route path="/lieux" element={<LieuxPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/connexion" element={<AuthPage />} />
            <Route path="/reinitialiser-mot-de-passe" element={<ResetPasswordPage />} />
            <Route path="/paiement/succes" element={<PaymentReturnPage />} />
            <Route path="/paiement/annule" element={<PaymentReturnPage />} />
            <Route path="/cgv" element={<CgvPage />} />
            <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
            <Route path="/politique-confidentialite" element={<PolitiqueConfidentialitePage />} />
            <Route path="/politique-cookies" element={<PolitiqueCookiesPage />} />
            <Route path="/compte" element={<RequireAuth><ClientLayout /></RequireAuth>}>
              <Route index element={<ClientDashboard />} />
              <Route path="evenements/:eventId" element={<EventDetailPage />} />
              <Route path="reservations/:reservationId" element={<ClientReservationPage />} />
              <Route path="profil" element={<ClientProfilePage />} />
            </Route>
          </Route>
          <Route path="/admin" element={<RequireAuth admin><AdminLayout /></RequireAuth>}>
            <Route index element={<AdminDashboard />} />
            <Route path="reservations" element={<ReservationsPage />} />
            <Route path="reservations/:ref" element={<ReservationDetailPage />} />
            <Route path="catalogue" element={<CatalogPage />} />
            <Route path="planning" element={<PlanningPage />} />
            <Route path="livraisons" element={<DeliveriesPage />} />
            <Route path="messages" element={<MessagesPage />} />
            <Route path="paiements" element={<PaymentsPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="reglages" element={<SettingsPage />} />
            <Route path="retours" element={<ReturnsPage />} />
            <Route path="cautions" element={<IncidentsPage />} />
            <Route path="clients" element={<ClientsPage />} />
            <Route path="clients/:customerId" element={<ClientDetailPage />} />
            <Route path="devis" element={<QuotesPage />} />
            <Route path="rendez-vous" element={<AppointmentsPage />} />
            <Route path="coordination" element={<CoordinationAdminPage />} />
            <Route path="coordination/:dossierId" element={<CoordinationDetailPage />} />
            <Route path="realisations" element={<GalleryAdminPage />} />
            <Route path="blog" element={<BlogAdminPage />} />
            <Route path="partenaires" element={<PartnersAdminPage />} />
            <Route path="formules-coordination" element={<FormulesCoordinationAdminPage />} />
            <Route path="mediatheque" element={<MediaAdminPage />} />
            <Route path="medias" element={<Navigate to="/admin/mediatheque" replace />} />
            <Route path="pages" element={<PagesSitePage />} />
            <Route path="parametres" element={<ParametresPage />} />
            <Route path="utilisateurs" element={<UtilisateursPage />} />
            <Route path="lieux" element={<VenuesAdminPage />} />
            <Route path="avis" element={<ReviewsAdminPage />} />
            <Route path="documents" element={<TermsAdminPage />} />
            <Route path="codes-promo" element={<PromoCodesPage />} />
            <Route path="emails" element={<EmailsAdminPage />} />
            <Route path="*" element={<AdminPlaceholder />} />
          </Route>
        </Routes>
      </BrowserRouter>
      </AuthProvider>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
